/**
 * Inactivity Tracker Service
 *
 * Monitors user activity and automatically logs out after 2 hours of inactivity.
 *
 * Tracks:
 * - Mouse movements and clicks
 * - Keyboard input
 * - Touch events (mobile)
 * - API requests (via integration)
 * - Tab visibility changes (handles browser tab freezing)
 *
 * Honda Cybersecurity Compliance: Auto-logout after 2 hours of inactivity
 */

export interface InactivityConfig {
  timeoutMs?: number // Timeout in milliseconds (default: 7200000ms = 2 hours)
  onTimeout: () => void // Callback when timeout is reached
  checkInterval?: number // How often to check for timeout (default: 60000ms = 1 min)
}

export class InactivityTracker {
  private config: InactivityConfig
  private lastActivityTime: number
  private checkIntervalId: number | null = null
  private eventListeners: Array<{element: any; event: string; handler: EventListener; useCapture: boolean}> = []
  private isTracking = false
  private wasHidden = false

  constructor(config: InactivityConfig) {
    this.config = {
      timeoutMs: 2 * 60 * 60 * 1000, // Default: 2 hours (7200000ms)
      checkInterval: 60000, // Default: check every minute
      ...config,
    }
    this.lastActivityTime = Date.now()
  }

  /**
   * Start tracking user activity
   */
  start(): void {
    if (this.isTracking) return

    this.isTracking = true
    this.lastActivityTime = Date.now()

    // Track user interactions
    this.addEventListeners()

    // Track tab visibility changes (for browser tab freeze detection)
    this.setupVisibilityTracking()

    // Start periodic check
    this.checkIntervalId = window.setInterval(() => {
      this.checkInactivity()
    }, this.config.checkInterval!)
  }

  /**
   * Stop tracking user activity
   */
  stop(): void {
    if (!this.isTracking) return

    this.isTracking = false

    // Remove all event listeners
    this.removeEventListeners()

    // Clear interval
    if (this.checkIntervalId !== null) {
      window.clearInterval(this.checkIntervalId)
      this.checkIntervalId = null
    }
  }

  /**
   * Reset the activity timer (call this on user activity or API requests)
   */
  resetTimer(): void {
    this.lastActivityTime = Date.now()
  }

  /**
   * Get time since last activity in milliseconds
   */
  getInactiveTime(): number {
    return Date.now() - this.lastActivityTime
  }

  /**
   * Get remaining time before timeout in milliseconds
   */
  getRemainingTime(): number {
    return Math.max(0, this.config.timeoutMs! - this.getInactiveTime())
  }

  /**
   * Check if user is inactive beyond timeout
   */
  private checkInactivity(): void {
    const inactiveTime = this.getInactiveTime()

    if (inactiveTime >= this.config.timeoutMs!) {
      this.stop()
      this.config.onTimeout()
    }
  }

  /**
   * Add event listeners for user interactions
   */
  private addEventListeners(): void {
    const activityHandler = () => this.resetTimer()

    // Mouse events
    this.addEventListener(document, 'mousemove', activityHandler)
    this.addEventListener(document, 'mousedown', activityHandler)
    this.addEventListener(document, 'click', activityHandler)

    // Keyboard events
    this.addEventListener(document, 'keydown', activityHandler)
    this.addEventListener(document, 'keypress', activityHandler)

    // Touch events (mobile)
    this.addEventListener(document, 'touchstart', activityHandler)
    this.addEventListener(document, 'touchmove', activityHandler)

    // Scroll events
    this.addEventListener(window, 'scroll', activityHandler, true) // Use capture for all scroll events

    // Focus events (user interaction with inputs)
    this.addEventListener(window, 'focus', activityHandler, true)
  }

  /**
   * Add event listener and track it for cleanup
   */
  private addEventListener(element: any, event: string, handler: EventListener, useCapture = false): void {
    element.addEventListener(event, handler, useCapture)
    this.eventListeners.push({element, event, handler, useCapture})
  }

  /**
   * Remove all tracked event listeners
   */
  private removeEventListeners(): void {
    this.eventListeners.forEach(({element, event, handler, useCapture}) => {
      element.removeEventListener(event, handler, useCapture)
    })
    this.eventListeners = []
  }

  /**
   * Setup tab visibility tracking to handle browser tab freeze
   * Modern browsers freeze inactive tabs - check activity when tab becomes visible
   */
  private setupVisibilityTracking(): void {
    const visibilityHandler = () => {
      if (document.hidden) {
        // Tab is hidden - browser may freeze timers
        this.wasHidden = true
      } else {
        // Tab is visible again
        if (this.wasHidden) {
          // Check if we should timeout based on actual time elapsed
          this.checkInactivity()
          this.wasHidden = false
        }
      }
    }

    this.addEventListener(document, 'visibilitychange', visibilityHandler)
  }

  /**
   * Get the configured timeout in milliseconds
   */
  getTimeoutMs(): number {
    return this.config.timeoutMs || 7200000 // Default to 2 hours if not set
  }
}

// Singleton instance
let trackerInstance: InactivityTracker | null = null

/**
 * Initialize the global inactivity tracker
 */
export function initInactivityTracker(config: InactivityConfig): InactivityTracker {
  if (trackerInstance) {
    trackerInstance.stop()
  }

  trackerInstance = new InactivityTracker(config)
  return trackerInstance
}

/**
 * Get the current tracker instance
 */
export function getInactivityTracker(): InactivityTracker | null {
  return trackerInstance
}

/**
 * Destroy the tracker instance
 */
export function destroyInactivityTracker(): void {
  if (trackerInstance) {
    trackerInstance.stop()
    trackerInstance = null
  }
}
